import React, {useState} from 'react';
import {
  IWorkFlowActivityItem,
  IWorkFlowStatus
} from '@shared/services/service-mappers/WorkflowMapper';
import {Avatar, Chip} from '@material-ui/core';
import HelperUtils from '@shared/utils/HelperUtils';
import {useTranslation} from 'react-i18next';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import CCMShowFullNotesDialog from 'src/modules/admin/modules/ChronicCareManagement/module-components/CCMShowFullNotesDialog/CCMShowFullNotesDialog';
import {
  TICKETING_SYSTEM_TICKET_ACTION_TYPES,
  TICKETING_SYSTEM_UPDATED_TICKET_FILED,
  TICKET_ACTION_TYPE_TO_ACTIVITY_TYPE_MAPPER,
  UPDATED_TICKET_DETAILS_TEXT
} from '@shared/constants/Mapping';
import {secondsToTimeFormat} from 'src/modules/admin/modules/ChronicCareManagement/module-components/StopTimerDialog/TimerUtils';
import {UserModelMapper} from '@shared/services/service-mappers/ModelMapper';
import './TicketActivityItem.scss';
import {
  formatTicketActivityItem,
  formatUpdatedKeyLabel
} from '../../../../helpers/utils';

export interface ITicketingSystemTicketActivityItemProps {
  item: IWorkFlowActivityItem;
  showActivityType?: boolean;
  renderTicketActivityItemContent?: (
    item: IWorkFlowActivityItem
  ) => JSX.Element;
  statuses?: IWorkFlowStatus[];
  allUsers?: UserModelMapper[];
}

const TicketActivityItem = (
  props: ITicketingSystemTicketActivityItemProps
): JSX.Element => {
  const {
    item,
    showActivityType = false,
    renderTicketActivityItemContent,
    allUsers = [],
    statuses = []
  } = props;
  const {t} = useTranslation();
  const formattedActivityItems = formatTicketActivityItem(item);

  const [showMoreDetails, setShowMoreDetails] = useState({
    open: false,
    title: '',
    value: ''
  });

  const handleCloseMoreDetails = () => {
    setShowMoreDetails({
      open: false,
      title: '',
      value: ''
    });
  };

  const renderValue = ({value}: {value?: string | number}) => {
    const charLimitOnOldAndNewValues = 500;

    if (!value) {
      return t('COMMON.NONE_VALUE');
    }
    if (value.toString().length <= charLimitOnOldAndNewValues) {
      return value;
    }

    return (
      <>
        {value.toString().slice(0, charLimitOnOldAndNewValues)}
        {'... '}
        <span
          onClick={() => {
            setShowMoreDetails({
              open: true,
              title: '',
              value: value.toString()
            });
          }}
          className="link-text-style">
          {t('TICKETING_SYSTEM.MORE_DETAILS')}
        </span>
      </>
    );
  };

  const getActivityTypeTransKey = (formattedItem: IWorkFlowActivityItem) => {
    return TICKET_ACTION_TYPE_TO_ACTIVITY_TYPE_MAPPER[
      formattedItem.type as keyof typeof TICKET_ACTION_TYPE_TO_ACTIVITY_TYPE_MAPPER
    ];
  };

  // render based on item.type
  const renderTicketActivityItemContentBasedOnType = (
    activityItem: IWorkFlowActivityItem
  ) => {
    if (activityItem?.type === TICKETING_SYSTEM_TICKET_ACTION_TYPES.WORK_LOG) {
      return (
        <div className="ticket-activity-content">
          <div className="ticket-activity-main-wrapper work-log-activity-item-wrapper">
            <p>
              <span>{t('TICKETING_SYSTEM.ACTIVITY.TIME_SPENT')}: </span>
              <span className="time-spent-wrapper">
                {activityItem?.metadata?.time_spent
                  ? `${secondsToTimeFormat(
                      (activityItem?.metadata?.time_spent as any) as number
                    )}`
                  : '-'}
              </span>
            </p>
            <div className="notes-wrapper">
              <p>{t('TICKETING_SYSTEM.ACTIVITY.NOTES')}:</p>
              {/* Using the below function to display the work log notes in an dialog it the limit exceeds */}
              {renderValue({
                value: (activityItem.metadata?.notes as any) as string
              })}
            </div>
          </div>
          <div className="ticket-value-diff-wrapper work-log-diff">
            <p className="old-value">
              {HelperUtils.formatDisplayDateTime(
                activityItem?.metadata?.start_time
              )}
            </p>
            <p className="separator">{'to'}</p>
            <p className="new-value">
              {HelperUtils.formatDisplayDateTime(
                activityItem?.metadata?.stop_time
              )}
            </p>
          </div>
        </div>
      );
    } else if (
      activityItem.type === TICKETING_SYSTEM_TICKET_ACTION_TYPES.CREATE_TICKET
    ) {
      return (
        <div className="ticket-activity-content">
          <p className="ticket-activity-main-wrapper">{activityItem.text}</p>
        </div>
      );
    } else if (
      activityItem.type ===
      TICKETING_SYSTEM_TICKET_ACTION_TYPES.CREATE_TICKET_ATTACHMENT
    ) {
      return (
        <div className="ticket-activity-content">
          <p className="ticket-activity-main-wrapper">{activityItem.text}</p>
          <div className="ticket-value-diff-wrapper">
            <p>{activityItem.metadata?.file_name || ''}</p>
          </div>
        </div>
      );
    } else if (
      activityItem.type === TICKETING_SYSTEM_TICKET_ACTION_TYPES.UPDATE_TICKET
    ) {
      const updatedFields = Object.keys(activityItem.metadata ?? {});
      if (updatedFields.length == 0) {
        return (
          <div className="ticket-activity-content">
            <p className="ticket-activity-main-wrapper">{activityItem.text}</p>
          </div>
        );
      }
      return updatedFields.map((updatedField, index) => {
        const defaultKeys = [
          TICKETING_SYSTEM_UPDATED_TICKET_FILED.TITLE,
          TICKETING_SYSTEM_UPDATED_TICKET_FILED.STATUS,
          TICKETING_SYSTEM_UPDATED_TICKET_FILED.DESCRIPTION,
          TICKETING_SYSTEM_UPDATED_TICKET_FILED.ASSIGNED_TO
        ];
        let newValue = activityItem.metadata?.[updatedField].new_value;
        let oldValue = activityItem.metadata?.[updatedField].old_value;
        if (updatedField === TICKETING_SYSTEM_UPDATED_TICKET_FILED.STATUS) {
          if (newValue) {
            const newStatus = statuses.find(status => status.id === newValue);
            if (newStatus) {
              newValue = newStatus.label;
            }
          }
          if (oldValue) {
            const oldStatus = statuses.find(status => status.id === oldValue);
            if (oldStatus) {
              oldValue = oldStatus.label;
            }
          }
        }
        if (
          updatedField === TICKETING_SYSTEM_UPDATED_TICKET_FILED.ASSIGNED_TO
        ) {
          if (newValue) {
            const newAssignedUser = allUsers.find(user => user.id == newValue);
            if (newAssignedUser) {
              newValue = HelperUtils.commaSeparatedFullName(
                newAssignedUser.first_name,
                newAssignedUser.last_name
              );
            }
          }
          if (oldValue) {
            const oldAssignedUser = allUsers.find(user => user.id == oldValue);
            if (oldAssignedUser) {
              oldValue = HelperUtils.commaSeparatedFullName(
                oldAssignedUser.first_name,
                oldAssignedUser.last_name
              );
            }
          }
        }
        if (
          defaultKeys.includes(updatedField) ||
          !renderTicketActivityItemContent
        ) {
          return (
            <div key={index} className="ticket-activity-content">
              <p className="ticket-activity-main-wrapper">
                {t('TICKETING_SYSTEM.ACTIVITY.UPDATED_LABEL')}{' '}
                {formatUpdatedKeyLabel(updatedField)}
              </p>
              <div className="ticket-value-diff-wrapper">
                <p className="old-value">
                  {renderValue({
                    value: oldValue
                  })}
                </p>
                <ArrowRightAltIcon />
                <p className="new-value">
                  {renderValue({
                    value: newValue
                  })}
                </p>
              </div>
              {activityItem?.text &&
                activityItem?.text != UPDATED_TICKET_DETAILS_TEXT && (
                  <p className="ticket-activity-text">{activityItem?.text}</p>
                )}
            </div>
          );
        }
        return renderTicketActivityItemContent(activityItem);
      });
    }
    return (
      <div className="ticket-activity-content">
        <p className="ticket-activity-main-wrapper">{activityItem.text}</p>
      </div>
    );
  };

  return (
    <>
      {formattedActivityItems.map((formattedItem, idx) => (
        <div key={idx} className={'ticketing-system-ticket-activity-item'}>
          <Avatar className="user-avatar">
            {formattedItem.created_user
              ? HelperUtils.shortName(
                  formattedItem.created_user.first_name,
                  formattedItem.created_user.last_name
                )
              : HelperUtils.shortName(t('APP.NAME'), '')}
          </Avatar>
          <div>
            <p className="ticket-activity-item-header">
              <span className="ticket-activity-item-user-name">
                {formattedItem.created_user
                  ? HelperUtils.commaSeparatedFullName(
                      formattedItem.created_user.first_name,
                      formattedItem.created_user.last_name
                    )
                  : t('APP.NAME')}
              </span>
              <span>&middot;</span>
              <span className="ticket-activity-item-created-time">
                {HelperUtils.formatDisplayDateTime(formattedItem.created_at)}
              </span>
              {showActivityType && (
                <Chip
                  className="ticket-activity-type-chip"
                  size="small"
                  label={
                    getActivityTypeTransKey(formattedItem)
                      ? t(getActivityTypeTransKey(formattedItem))
                      : t('TICKETING_SYSTEM.ACTIVITY.HISTORY')
                  }
                />
              )}
            </p>
            {renderTicketActivityItemContentBasedOnType(formattedItem)}
          </div>
          {showMoreDetails.open && (
            <CCMShowFullNotesDialog
              open={showMoreDetails.open}
              onClose={handleCloseMoreDetails}
              notes={showMoreDetails.value}
              title={showMoreDetails.title}
            />
          )}
        </div>
      ))}
    </>
  );
};

export default TicketActivityItem;
